import { computed, watch } from "@vue/composition-api";
import Vue from "vue";
import useAuthUser from "@/hooks/useAuthUser";
import useNSFWUserSettings from "@/hooks/useNSFWUserSettings";
import { IS_BROWSER } from "@/lib/browserUtils";
import { getNsfwAgeGateCookie, leaveSite, NSFW_AGE_GATE, setNsfwAgeGateCookie } from "@/lib/nsfwHelpers";
import config from "@/config";

const state = Vue.observable({
  answer: null as NSFW_AGE_GATE | null,
});

export default function useNsfwEntryGate() {
  // cookie is the source of truth: it is shared with SSR, nginx cache and the landing script (public/age-gate.js)
  if (IS_BROWSER) state.answer = getNsfwAgeGateCookie();
  const { authUser } = useAuthUser();
  const { nSFWUserSettings, updateNSFWUserSettings } = useNSFWUserSettings();

  // on this site 18+ entry gate is a part of NSFW addon
  const gateEnabled = computed(() => config.featureNSFW);
  // logged in user who already allowed NSFW on the server passes without the gate
  const allowedByUserSettings = computed(() => !!authUser.value && nSFWUserSettings.value === true);
  const showGate = computed(
    () =>
      gateEnabled.value &&
      (state.answer === NSFW_AGE_GATE.DECLINED ||
        (state.answer !== NSFW_AGE_GATE.PASSED && !allowedByUserSettings.value))
  );

  const setAnswer = (value: NSFW_AGE_GATE) => {
    setNsfwAgeGateCookie(value);
    state.answer = value;
  };

  const enter = async () => {
    // guest has no server setting: mark NSFW as allowed so ConfirmSensitiveContent doesn't ask again
    if (!authUser.value && nSFWUserSettings.value === null) await updateNSFWUserSettings(true);
    setAnswer(NSFW_AGE_GATE.PASSED);
  };

  const exit = () => {
    setAnswer(NSFW_AGE_GATE.DECLINED);
    leaveSite();
  };

  watch(
    allowedByUserSettings,
    value => {
      if (value && gateEnabled.value && state.answer === null) setAnswer(NSFW_AGE_GATE.PASSED);
    },
    { immediate: true }
  );

  return {
    showGate,
    declined: computed(() => state.answer === NSFW_AGE_GATE.DECLINED),
    enter,
    exit,
  };
}
